import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Spin } from "antd";
import { Download, Eye } from "lucide-react";
import * as XLSX from "xlsx";
import styles from "./OriginalDocumentPreview.module.css";

const MAX_ROWS = 300;
const MAX_COLUMNS = 80;

const getEndpoint = (type, id) =>
  type === "order" ? `/api/getOriginFile/${id}` : `/api/getOriginFileOffer/${id}`;

const fileNameFromDisposition = (value) => {
  if (!value) return "";
  const utfMatch = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch) {
    try {
      return decodeURIComponent(utfMatch[1].replace(/['"]/g, ""));
    } catch {
      return utfMatch[1];
    }
  }
  return value.match(/filename=["']?([^;"']+)/i)?.[1]?.trim() || "";
};

const extensionOf = (name) => String(name || "").split(".").pop()?.toLowerCase() || "";

const detectKind = (mime, name, inputMode) => {
  const normalizedMime = String(mime || "").toLowerCase();
  const extension = extensionOf(name);
  const mode = String(inputMode || "").toLowerCase();

  if (normalizedMime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(extension) || ["image", "photo"].includes(mode)) return "image";
  if (normalizedMime.includes("pdf") || extension === "pdf" || mode === "pdf") return "pdf";
  if (normalizedMime.includes("spreadsheet") || normalizedMime.includes("excel") || ["xls", "xlsx", "xlsm", "csv"].includes(extension) || mode === "excel") return "excel";
  if (normalizedMime.startsWith("text/") || ["txt", "csv", "tsv"].includes(extension) || mode === "text") return "text";
  return "unsupported";
};

export default function OriginalDocumentPreview({ open, document, type, onClose, onOpenDetails }) {
  const [state, setState] = useState({ loading: false, error: "", kind: "", fileName: "", mime: "", blob: null, url: "", workbook: null, text: "" });
  const [sheetName, setSheetName] = useState("");
  const inputMode = document?.input_mode ?? document?.inputMode;

  useEffect(() => {
    if (!open || !document?.id) return undefined;
    const controller = new AbortController();
    let objectUrl = "";
    setState({ loading: true, error: "", kind: "", fileName: "", mime: "", blob: null, url: "", workbook: null, text: "" });
    setSheetName("");

    const load = async () => {
      try {
        const response = await fetch(getEndpoint(type, document.id), { signal: controller.signal });
        if (!response.ok) throw new Error(`Сервер вернул ошибку ${response.status}`);
        const blob = await response.blob();
        const mime = response.headers.get("content-type") || blob.type || "";
        const fileName = fileNameFromDisposition(response.headers.get("content-disposition")) || document.name || `document-${document.id}`;
        const kind = detectKind(mime, fileName, inputMode);
        objectUrl = URL.createObjectURL(blob);

        if (kind === "excel") {
          const workbook = XLSX.read(await blob.arrayBuffer(), { type: "array", cellDates: true });
          setSheetName(workbook.SheetNames[0] || "");
          setState({ loading: false, error: "", kind, fileName, mime, blob, url: objectUrl, workbook, text: "" });
          return;
        }
        if (kind === "text") {
          setState({ loading: false, error: "", kind, fileName, mime, blob, url: objectUrl, workbook: null, text: await blob.text() });
          return;
        }
        setState({ loading: false, error: "", kind, fileName, mime, blob, url: objectUrl, workbook: null, text: "" });
      } catch (error) {
        if (error.name !== "AbortError") {
          setState((current) => ({ ...current, loading: false, error: error.message || "Не удалось загрузить исходный документ" }));
        }
      }
    };

    load();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, document?.id, document?.name, inputMode, type]);

  const sheetRows = useMemo(() => {
    const sheet = state.workbook?.Sheets?.[sheetName];
    if (!sheet) return [];
    return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false }).slice(0, MAX_ROWS).map((row) => row.slice(0, MAX_COLUMNS));
  }, [sheetName, state.workbook]);

  const download = () => {
    if (!state.url) return;
    const link = window.document.createElement("a");
    link.href = state.url;
    link.download = state.fileName;
    link.click();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(1400px, 96vw)"
      destroyOnClose
      title={
        <div className={styles.title}>
          <strong>Исходный документ</strong>
          <span>{state.fileName || document?.name || `ID ${document?.id || ""}`}</span>
        </div>
      }
    >
      <div className={styles.toolbar}>
        {state.workbook?.SheetNames?.length > 1 && (
          <div className={styles.sheets} aria-label="Листы документа">
            {state.workbook.SheetNames.map((name) => (
              <button key={name} type="button" className={name === sheetName ? styles.sheetActive : ""} onClick={() => setSheetName(name)}>{name}</button>
            ))}
          </div>
        )}
        <div className={styles.actions}>
          <Button icon={<Eye size={16} />} onClick={onOpenDetails}>Обработанные данные</Button>
          <Button type="primary" icon={<Download size={16} />} disabled={!state.url} onClick={download}>Скачать оригинал</Button>
        </div>
      </div>

      <div className={styles.preview}>
        {state.loading && <div className={styles.center}><Spin size="large" /><span>Загружаем исходный документ…</span></div>}
        {state.error && <Alert type="error" showIcon message="Не удалось открыть исходный документ" description={state.error} />}
        {!state.loading && !state.error && state.kind === "excel" && (
          <div className={styles.tableViewport}>
            <table>
              <tbody>
                {sheetRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <th>{rowIndex + 1}</th>
                    {row.map((cell, columnIndex) => <td key={columnIndex}>{String(cell)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {!sheetRows.length && <div className={styles.center}>Лист не содержит данных</div>}
          </div>
        )}
        {!state.loading && !state.error && state.kind === "pdf" && <iframe className={styles.frame} src={state.url} title={`Исходный документ ${state.fileName}`} />}
        {!state.loading && !state.error && state.kind === "image" && <div className={styles.imageViewport}><img src={state.url} alt={state.fileName} /></div>}
        {!state.loading && !state.error && state.kind === "text" && <pre className={styles.textPreview}>{state.text}</pre>}
        {!state.loading && !state.error && state.kind === "unsupported" && <Alert type="info" showIcon message="Предпросмотр этого формата недоступен" description="Оригинальный файл можно скачать и открыть в подходящей программе." />}
      </div>
      {state.kind === "excel" && sheetRows.length >= MAX_ROWS && <p className={styles.limitNote}>Для быстрого просмотра показаны первые {MAX_ROWS} строк листа.</p>}
    </Modal>
  );
}
